import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import React from "react"; 
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination, Keyboard } from "swiper";
import PropTypes from "prop-types";
import { Box, Card, CardContent, CardMedia, Typography, Link } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

const MobilePopupCarousel = ({ data, slidesPerView, spaceBetween, navigation, pagination }) => {
  const hasMeaningfulContent = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent.trim().length > 0;
  };
  
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
    {data.length > 1 && (
    <>
      {/* Custom Prev Button */}
      <Box
        className="custom-prev"
        sx={{
          position: "absolute",
          top: "94%",
          left: 8,
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "rgba(116, 116, 116, 0.3)",
          borderRadius: "50%",
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.3)",
          },
        }}
      >
        <ArrowBackIosNew sx={{ color: "white", fontSize: 16 }} />
      </Box>

      {/* Custom Next Button */}
      <Box
        className="custom-next"
        sx={{
          position: "absolute",
          top: "94%",
          right: 8,
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "rgba(116, 116, 116, 0.3)",
          borderRadius: "50%",
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.3)",
          },
        }}
      >
        <ArrowForwardIos sx={{ color: "white", fontSize: 16 }} />
      </Box>
    </>
    )}
      
    <Swiper
      spaceBetween={spaceBetween}
      // autoHeight={true}
      slidesPerView={slidesPerView}
      navigation={{
        prevEl: ".custom-prev",
        nextEl: ".custom-next",
      }}
      pagination={pagination ? { clickable: true } : false}
      keyboard={true}
      autoplay={{
        delay: 3000,
        disableOnInteraction: false,
      }}
      modules={[Autoplay, Navigation, Pagination, Keyboard]}
    >
      {data.map((item, index) => {
        // When a slide has text below the image, cap the image height so the
        // start of the text always peeks into the fixed-height card, cueing the
        // user that there is more content to scroll. Slides with no text keep
        // the image at its natural size.
        const hasText =
          (item.title && item.title.trim()) ||
          (item.content && hasMeaningfulContent(item.content));

        return (
        <SwiperSlide key={index}>
          <Card
            sx={{
              width: '100%',
              height: '300px',
              maxHeight: '75vh',
              margin: 'auto',
              maxWidth: '600px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box padding="10px">
              <Link href={item.link} target="_blank" rel="noopener noreferrer">
                <CardMedia
                  component="img"
                  image={item.imageUrl}
                  alt={item.title}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    // Fill the width with `cover` (crops a sliver off top/bottom
                    // rather than letterboxing) to minimise whitespace without
                    // distorting. When there's text, cap the height so a strip
                    // of it peeks into the fixed-height card as a scroll cue.
                    objectFit: 'cover',
                    ...(hasText && { maxHeight: '210px' }),
                  }}
                />
              </Link>
            </Box>

            {hasText ? (
            <CardContent
              sx={{
                paddingTop: 0.5,
                textAlign: 'left',
                color: process.env.REACT_APP_COLOR_TEXT || 'white',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {item.title && item.title.trim() && (
                <Typography
                  variant="subtitle1"
                  component="h2"
                  sx={{ color: process.env.REACT_APP_COLOR_TEXT || 'white', wordWrap: "break-word", fontWeight: "bold", }}
                >
                  {item.title}
                </Typography>
              )}

              {item.content && hasMeaningfulContent(item.content) && (
                <Typography
                  variant="body2"
                  sx={{
                    color: process.env.REACT_APP_COLOR_TEXT || 'white',
                    wordWrap: "break-word",
                    flex: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              )}
            </CardContent>              
            ) : null}     
          </Card>
          {data.length > 1 && (
          <Box
            sx={{
              height: "40px",
              marginTop: "-2px",
              backgroundColor:process.env.REACT_APP_COLOR_BACKGROUND_PAPER || "#313338",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            }}
          />
          )}
        </SwiperSlide>
        );
      })}
    </Swiper>
    </Box>
  );
};

MobilePopupCarousel.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      imageUrl: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ).isRequired,
  slidesPerView: PropTypes.number,
  spaceBetween: PropTypes.number,
  navigation: PropTypes.bool,
  pagination: PropTypes.bool,
};

MobilePopupCarousel.defaultProps = {
  slidesPerView: 1,
  spaceBetween: 20,
  navigation: true,
  pagination: true,
};

export default MobilePopupCarousel;
